import { create } from 'zustand';
import { persist } from 'zustand/middleware';
export type BrowserNotificationPermission = NotificationPermission | 'unsupported';
export type NotifyOptions = {
  body?: string;
  tag?: string;
  onClick?: () => void;
};
type BrowserNotificationsState = {
  isEnabled: boolean;
  nudgeDismissed: boolean;
  permissionVersion: number;
  setEnabled: (isEnabled: boolean) => void;
  dismissNudge: () => void;
  bumpPermissionVersion: () => void;
};
export const useBrowserNotificationsStore = create<BrowserNotificationsState>()(persist(set => ({
  isEnabled: false,
  nudgeDismissed: false,
  permissionVersion: 0,
  setEnabled: isEnabled => {
    set({
      isEnabled
    });
  },
  dismissNudge: () => {
    set({
      nudgeDismissed: true
    });
  },
  // Nothing reads permissionVersion: bumping it exists to re-render consumers once the browser
  // prompt is answered, and on the denied path it's the only signal (`setEnabled` never fires).
  // So consumers must subscribe to the whole store — a selector would drop that case silently.
  bumpPermissionVersion: () => {
    set(state => ({
      permissionVersion: state.permissionVersion + 1
    }));
  }
}), {
  name: 'dashboard.browser-notifications',
  partialize: state => ({
    isEnabled: state.isEnabled,
    nudgeDismissed: state.nudgeDismissed
  }),
  merge: (persisted, current) => {
    const stored = persisted as Partial<BrowserNotificationsState> | undefined;
    const storedIsEnabled = stored?.isEnabled;
    const storedNudgeDismissed = stored?.nudgeDismissed;
    return {
      ...current,
      isEnabled: typeof storedIsEnabled === 'boolean' ? storedIsEnabled : current.isEnabled,
      nudgeDismissed: typeof storedNudgeDismissed === 'boolean' ? storedNudgeDismissed : current.nudgeDismissed
    };
  }
}));
const getPermission = (): BrowserNotificationPermission => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
};
const useBrowserNotifications = () => {
  const {
    isEnabled,
    nudgeDismissed,
    setEnabled,
    dismissNudge,
    bumpPermissionVersion
  } = useBrowserNotificationsStore();
  const permission = getPermission();
  const requestPermission = async (): Promise<BrowserNotificationPermission> => {
    const current = getPermission();
    if (current !== 'default') return current;
    const result = await Notification.requestPermission();
    if (result === 'granted') setEnabled(true);
    bumpPermissionVersion();
    return result;
  };
  const notify = (title: string, options?: NotifyOptions) => {
    if (getPermission() !== 'granted' || !useBrowserNotificationsStore.getState().isEnabled) return;
    if (!document.hidden && document.hasFocus()) return;
    try {
      const notification = new Notification(title, {
        body: options?.body,
        tag: options?.tag,
        icon: '/favicon.ico'
      });
      notification.onclick = () => {
        window.focus();
        options?.onClick?.();
      };
    } catch {}
  };
  return {
    permission,
    isEnabled,
    nudgeDismissed,
    setEnabled,
    dismissNudge,
    requestPermission,
    notify
  };
};
export default useBrowserNotifications;